import moment from "moment";
import {
  Col,
  Radio,
  Row,
  Form,
  Select,
  Tooltip,
  Table,
  Tag,
  Pagination,
  Typography,
  RadioChangeEvent,
  DatePicker,
  Popover,
  Slider,
} from "antd";
import Avatar from "antd/lib/avatar/avatar";
import { ReactComponent as ExportIcon } from "shared/assets/images/cloud-download.svg";
import { ColumnsType } from "antd/lib/table";
import { debounce } from "lodash";
import {
  exportTrackRankingCSV,
  exportTrackRankingExcel,
  fetchArtistByName,
  fetchLabelByName,
  fetchReleaseListByName,
} from "radar/Service/RadarService";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Card, Loader, FieldSelect } from "shared/components";
import {
  RADAR_TRACKS_RANKING_FILTER,
  RADAR_TRACKS_RANKING_HIGHLIGHTED_ARTISTS,
  RADAR_TRACKS_RANKING_INDEX,
} from "shared/constants/LocalStorageConsts";
import { getI18nCoutry } from "shared/helpers/i18nCountry";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import { getLocaleDatePickerByI18n } from "shared/helpers/getLocaleDatePickerByI18n";
import { I18NColumn } from "../I18nColumn";
import { ListColumn } from "../ListColumn";
import { InfoCircleOutlined } from "@ant-design/icons";
import RadioButtonRankingOptions from "../radio-button-ranking-options/RadioButtonRankingOptions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faChevronDown,
  faChevronUp,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import { ptBRFormat } from "shared/helpers/BRLFormat";
import { FileType, downloadFile } from "shared/helpers/download-file";
import {
  TrackRankingIndex,
  clearTracksRanking,
  getTrackRanking,
} from "explorer/reducers/rankings";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";

const { RangePicker } = DatePicker;

const defaults = {
  defaultForm: {
    labels: [],
    genreStrm: [],
    country: [],
    artistIdStrm: [],
    track: [1, 100],
    releaseAgeTimeFrame: null,
    releases: [],
    tempo: [0, 250],
    releaseType: null,
    danceabilityLevel: null,
    energyLevel: null,
    valenceLevel: null,
    acousticnessLevel: null,
    livenessLevel: null,
  },
  defaultFilter: {
    labels: [],
    genreStrm: [],
    country: [],
    artistIdStrm: [],
    releases: [],
    tempoInit: 0,
    tempoEnd: 250,
    releaseAgeTimeFrame: "",
    offset: 0,
    limit: 50,
  },
};

const initial = () => {
  const savedFilter = localStorage.getItem(RADAR_TRACKS_RANKING_FILTER);
  const highlightedArtists = localStorage.getItem(
    RADAR_TRACKS_RANKING_HIGHLIGHTED_ARTISTS
  );
  const defaultArtists: any = highlightedArtists
    ? JSON.parse(highlightedArtists)
    : [];
  if (savedFilter) {
    const parsed = JSON.parse(savedFilter);

    const { trackInit, trackEnd, tempoInit, tempoEnd, ...rest } = parsed;
    return {
      defaultForm: {
        ...rest,
        track: [trackInit, trackEnd],
        tempo: [tempoInit, tempoEnd],
        releaseAgeTimeCustom: parsed.releaseAgeTimeCustom
          ? [
              moment(parsed.releaseAgeTimeCustom[0]),
              moment(parsed.releaseAgeTimeCustom[1]),
            ]
          : null,
      },
      defaultFilter: parsed,
      defaultArtists,
      defaultRangeFilter: parsed.defaultRangeFilter
        ? parsed.defaultRangeFilter
        : "week",
    };
  }

  return {
    ...defaults,
    defaultArtists,
    defaultRangeFilter: null,
  };
};

const timeFrameOptions = [
  "oneWeek",
  "twoWeek",
  "oneMonth",
  "threeMonth",
  "sixMonth",
  "oneYear",
  "twoYear",
  "threeYear",
  "fourYear",
  "fiveYear",
  "tenYear",
  "2000s",
  "90s",
  "80s",
  "70s",
  "60s",
  "50s",
  "custom",
];

const TIME_FRAME_OPTION_CUSTOM = 17;

const rankingOptionsWithTop: Array<string> = [
  TrackRankingIndex.Top200,
  TrackRankingIndex.Top1000,
];

const TracksRanking: React.FC<RouteComponentProps> = () => {
  const savedIndex = localStorage.getItem(
    RADAR_TRACKS_RANKING_INDEX
  ) as TrackRankingIndex;
  const dispatch = useDispatch();
  const { defaultFilter, defaultForm, defaultArtists, defaultRangeFilter } =
    initial();
  const { t, i18n } = useTranslation("radar");
  const [form] = Form.useForm();
  const [releaseAgeTimeFrameFieldValue, setReleaseAgeTimeFrameFieldValue] =
    useState<any>(defaultFilter?.releaseAgeTimeFrame ?? "");
  const [filter, setFilter] = useState<any>(defaultFilter);
  const [rankingIndex, setRankingIndex] = useState<TrackRankingIndex>(
    savedIndex ? savedIndex : TrackRankingIndex.Popularity
  );

  const [enableExport, setEnableExport] = useState<boolean>(false);
  const [loadingLocal, setLoadingLocal] = useState<boolean>(false);
  const [loadingLabels, setLoadingLabels] = useState<boolean>(false);
  const [loadingArtist, setLoadingArtist] = useState<boolean>(false);
  const [loadingReleases, setLoadingReleases] = useState<boolean>(false);
  const [releaseOptions, setReleaseOptions] = useState<any>([]);
  const [labelCache, setLabelCache] = useState<any>([]);
  const [labelOptions, setLabelOptions] = useState<any>([]);
  const [labelSearch, setLabelSearch] = useState<string>("");
  const [releaseSearch, setReleaseSearch] = useState<string>("");
  const [showAudioFeaturesFilter, setShowAudioFeaturesFilter] =
    useState<boolean>(false);
  const [searchedArtists, setSearchedArtists] = useState<any>([]);
  const [artistsOptions, setArtistsOptions] = useState<any>(
    defaultArtists.map(({ artistIdStrm, artistName, imagesSpotify }: any) => ({
      id: artistIdStrm,
      name: artistName,
      image: (imagesSpotify && imagesSpotify[2]?.url) || "",
    }))
  );
  const [rangeFilter, setRangeFilter] = useState<string>(
    defaultRangeFilter ? defaultRangeFilter : "week"
  );

  const { countries, countryOptions } = useSelector(
    (state: any) => state.countries
  );
  const { genres, genreOptions } = useSelector((state: any) => state.genres);
  const { tracksRanking, loading } = useSelector(
    (state: any) => state.rankings
  );
  const getRelease = async (term: string) => {
    const cleanTerm = term.replace(/[/\\#,+()$~%.'":*?<>{}]/g, "");
    if (cleanTerm) {
      setLoadingReleases(true);
      setReleaseOptions([]);
      const response = await fetchReleaseListByName(cleanTerm);
      setReleaseOptions(response);
      setLoadingReleases(false);
    }
  };

  const getLabels = async (term: string, defaultOption?: any) => {
    const cleanTerm = term.replace(/[/\\#,+()$~%.'":*?<>{}]/g, "");
    if (cleanTerm) {
      setLoadingLabels(true);
      const response: any = await fetchLabelByName(cleanTerm);
      setLabelOptions([...labelCache, ...response]);
      setLoadingLabels(false);
    }
  };

  const getArtists = async (artistNameTerm: string) => {
    if (artistNameTerm.replace(/[/\\#,+()$~%.'":*?<>{}]/g, "")) {
      setLoadingArtist(true);
      setArtistsOptions([]);
      const response = await fetchArtistByName({ artistNameTerm });
      setSearchedArtists(response);
      setLoadingArtist(false);
    }
  };

  const colunas: ColumnsType<any> = [
    {
      dataIndex: "position",
      width: "2%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      dataIndex: "positionDif",
      align: "center",
      render: (value, record: any) => {
        if (!record.position) return "N/A";
        return (
          <span
            className={`position-difference ${
              value === null
                ? "blue"
                : value > 0
                ? "green"
                : value < 0
                ? "red"
                : ""
            }`}
          >
            {value === null ? (
              "New"
            ) : value === 0 ? (
              <FontAwesomeIcon icon={faStop} />
            ) : value > 0 ? (
              <>
                <FontAwesomeIcon icon={faArrowUp} />
                {` ${value}`}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faArrowDown} />
                {` ${Math.abs(value)}`}
              </>
            )}
          </span>
        );
      },
      width: "3%",
    },
    {
      title: t("pages.TracksRanking.trackName"),
      dataIndex: "trackName",
      width: "10%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      title: t("pages.ReleasesRanking.release"),
      dataIndex: "releaseName",
      width: "10%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      title: t("pages.TracksRanking.trackType"),
      dataIndex: "releaseType",
      width: "5%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      title: t("pages.ReleasesRanking.labelName"),
      dataIndex: "labelName",
      width: "10%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      title: t("pages.ReleasesRanking.ArtistName"),
      dataIndex: "artistNames",
      width: "5%",
      align: "center",
      render: (value) => (
        <ListColumn
          listRow={value}
          label={t("pages.ReleasesRanking.ArtistName")}
          t={t}
        />
      ),
    },
    {
      title: t("pages.ArtistsRanking.country"),
      dataIndex: "country",
      width: "10%",
      align: "center",
      render: (value) => (
        <I18NColumn
          listRow={value ? [...value].sort() : []}
          label={t("pages.ArtistsRanking.country")}
          i18nFunction={(country: string) =>
            getI18nCoutry(country, i18n, countries)
          }
          t={t}
        />
      ),
    },
    {
      title: t("pages.ArtistsRanking.genres"),
      dataIndex: "genresStrm",
      width: "10%",
      align: "center",
      render: (value) => (
        <I18NColumn
          listRow={value ? [...value].sort() : []}
          label={t("pages.ArtistsRanking.genres")}
          i18nFunction={(genre: string) => getI18nGenre(genre, i18n, genres)}
          t={t}
        />
      ),
    },
    {
      title: rankingOptionsWithTop.includes(rankingIndex)
        ? t("pages.ReleasesRanking.popularityTableWithVariation")
        : t("pages.ReleasesRanking.popularityTable"),
      width: "5%",
      render: (value) => {
        return (
          <>
            {value.popularityIndex}
            {value.positionDif !== 0 &&
              value.positionDif !== null &&
              rankingOptionsWithTop.includes(rankingIndex) && (
                <>
                  {value.positionDif > 0 ? (
                    <span style={{ color: "green", fontWeight: "bold" }}>
                      (+{value.positionDif})
                    </span>
                  ) : (
                    <span style={{ color: "red", fontWeight: "bold" }}>
                      ({value.positionDif})
                    </span>
                  )}
                </>
              )}
          </>
        );
      },
    },
    {
      title: t("pages.ReleasesRanking.ageInDays"),
      dataIndex: "releaseDays",
      width: "5%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
  ];

  const onDeleteClick = () => {
    dispatch(clearTracksRanking());
    form.setFieldsValue(defaults.defaultForm);
    setFilter(defaults.defaultFilter);
    setReleaseAgeTimeFrameFieldValue("");
  };

  const onChangeIndex = (evt: any) => {
    const saveRankingIndex = evt.target.value;
    console.log(saveRankingIndex);
    localStorage.setItem(RADAR_TRACKS_RANKING_INDEX, saveRankingIndex);
    setRankingIndex(saveRankingIndex);
  };

  const onChangeArtist = async (value: any) => {
    const highlightedArtists = localStorage.getItem(
      RADAR_TRACKS_RANKING_HIGHLIGHTED_ARTISTS
    );
    const artist: any = searchedArtists.filter((opt: any) =>
      value.includes(opt.artistIdStrm)
    );
    if (highlightedArtists) {
      const parsed = JSON.parse(highlightedArtists);
      localStorage.setItem(
        RADAR_TRACKS_RANKING_HIGHLIGHTED_ARTISTS,
        JSON.stringify([...parsed, ...artist])
      );
    } else {
      localStorage.setItem(
        RADAR_TRACKS_RANKING_HIGHLIGHTED_ARTISTS,
        JSON.stringify([artist])
      );
    }
  };

  const onSelectRelease = (value: any) => {
    if (value === "all") {
      const filteredReleases = releaseOptions.filter((release: any = "") =>
        release.includes(releaseSearch)
      );
      form.setFieldsValue({ releases: filteredReleases });
    }
  };

  const onSelectLabel = (value: any) => {
    if (value === "all") {
      const filteredLabels = labelOptions.filter((label: any = "") =>
        label.includes(labelSearch)
      );
      form.setFieldsValue({ labels: filteredLabels });
    }
  };

  const onSelectGenreStrm = (value: any) => {
    if (value === "all") {
      form.setFieldsValue({ genreStrm: genreOptions.map((x: any) => x.value) });
    }
  };

  const onFinishFilter = () => {
    const {
      country,
      releaseAgeTimeFrame,
      track,
      releaseAgeTimeCustom,
      tempo,
      ...rest
    } = form.getFieldsValue(true);
    const [trackEnd, trackInit] = track;
    const [tempoInit, tempoEnd] = tempo;
    const saveFilter = {
      ...defaultFilter,
      country: country?.length === 0 ? undefined : country,
      trackEnd,
      trackInit,
      releaseAgeTimeFrame,
      releaseAgeTimeCustom,
      tempoInit,
      tempoEnd,
      ...rest,
    };
    dispatch(clearTracksRanking());
    setArtistsOptions([]);
    localStorage.setItem(
      RADAR_TRACKS_RANKING_FILTER,
      JSON.stringify(saveFilter)
    );
    setFilter(saveFilter);
  };

  const getFormattedCustomTimeFrameFromForm = (formFilter: any) => {
    const isReleaseAgeCustom =
      formFilter.releaseAgeTimeFrame === TIME_FRAME_OPTION_CUSTOM;
    if (formFilter?.releaseAgeTimeCustom)
      return {
        releaseAgeCustomInit: isReleaseAgeCustom
          ? formFilter?.releaseAgeTimeCustom[0]
          : null,
        releaseAgeCustomEnd: isReleaseAgeCustom
          ? formFilter?.releaseAgeTimeCustom[1]
          : null,
      };

    return {
      releaseAgeCustomInit: null,
      releaseAgeCustomEnd: null,
    };
  };

  const onChangeRangeFilter = (evt: RadioChangeEvent) => {
    const savedFilter = localStorage.getItem(RADAR_TRACKS_RANKING_FILTER);
    const savedFilteParsed = JSON.parse(savedFilter ? savedFilter : "{}");

    localStorage.setItem(
      RADAR_TRACKS_RANKING_FILTER,
      JSON.stringify({
        ...savedFilteParsed,
        defaultRangeFilter: evt.target.value,
      })
    );

    setRangeFilter(evt.target.value);
  };

  const enableRangeFilter = (currentRanking: string) =>
    rankingOptionsWithTop.includes(currentRanking);

  const formatDate = (date: string) =>
    date && moment(date).format("DD/MM/YYYY");

  const downloadRankingReportCSV = async () => {
    setLoadingLocal(true);
    const releaseAgeTimeFrameParams =
      getFormattedCustomTimeFrameFromForm(filter);
    const combinedFilter = {
      ...filter,
      sortMode: rankingIndex,
      timeFrame: enableRangeFilter(rankingIndex) ? rangeFilter : null,
      ...releaseAgeTimeFrameParams,
    };
    const data: any = await exportTrackRankingCSV(combinedFilter);
    downloadFile(data, "tracks-ranking.csv", FileType.Csv);
    setLoadingLocal(false);
    setEnableExport(false);
  };

  const downloadRankingReportExcel = async () => {
    setLoadingLocal(true);
    const releaseAgeTimeFrameParams =
      getFormattedCustomTimeFrameFromForm(filter);
    const combinedFilter = {
      ...filter,
      sortMode: rankingIndex,
      timeFrame: enableRangeFilter(rankingIndex) ? rangeFilter : null,
      ...releaseAgeTimeFrameParams,
    };
    const data: any = await exportTrackRankingExcel(combinedFilter);
    downloadFile(data, "tracks-ranking.xlsx", FileType.Xlsx);
    setLoadingLocal(false);
    setEnableExport(false);
  };

  useEffect(() => {
    dispatch(
      getTrackRanking({
        ...getFormattedCustomTimeFrameFromForm(filter),
        filter,
        rankingIndex,
        timeFrame: enableRangeFilter(rankingIndex) ? rangeFilter : null,
      })
    );
  }, [rankingIndex, filter, rangeFilter, dispatch]);

  useEffect(() => {
    setArtistsOptions(
      searchedArtists.map(
        ({ artistIdStrm, artistName, imagesSpotify }: any) => ({
          id: artistIdStrm,
          name: artistName,
          image: (imagesSpotify && imagesSpotify[2]?.url) || "",
        })
      )
    );
  }, [searchedArtists]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Ranking.S4L_RANKING_TRACKS,
    });
  }, []);

  return (
    <div className="radar-home">
      <Row style={{ marginTop: "16px" }}>
        <Col style={{ textAlign: "center" }}>
          <h1 className="ranking-title">{t("pages.ArtistsRanking.title")}</h1>
        </Col>
      </Row>
      <Row style={{ marginTop: "16px" }}>
        <Col style={{ textAlign: "center" }}>
          <RadioButtonRankingOptions value="track" />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} md={24} className="conteudo-home">
          <Form
            form={form}
            initialValues={defaultForm}
            onFinish={onFinishFilter}
            className="filtro"
            layout="vertical"
          >
            <Card>
              <Row gutter={24}>
                <Col xs={24} md={5}>
                  <Form.Item
                    name="labels"
                    label={t("pages.ReleasesRanking.label")}
                  >
                    <FieldSelect
                      mode="multiple"
                      filterOption={false}
                      onSearch={debounce(getLabels, 1000)}
                      onDropdownVisibleChange={(_: any) => setLabelSearch("")}
                      onSelect={onSelectLabel}
                      onChange={(value: any) => setLabelCache(value)}
                      notFoundContent={
                        <Loader.NotFoundLoader loading={loadingLabels} />
                      }
                      allowClear
                      dataList={labelOptions.map((option: any) => {
                        return {
                          label: option,
                          value: option,
                        };
                      })}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                      showSelectAll={false}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={5}>
                  <Form.Item
                    name="genreStrm"
                    label={t("pages.ArtistsRanking.genre")}
                  >
                    <FieldSelect
                      mode="multiple"
                      allowClear
                      onSelect={onSelectGenreStrm}
                      dataList={genreOptions}
                      showSelectAll
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={5}>
                  <Form.Item
                    name="country"
                    label={t("pages.ReleasesRanking.country")}
                  >
                    <FieldSelect
                      mode="multiple"
                      showSelectAll={false}
                      optionFilterProp="children"
                      filterOption={(input: string, option: any) =>
                        option?.children
                          ?.toUpperCase()
                          ?.includes(input.toUpperCase())
                      }
                      dataList={countryOptions}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={5}>
                  <Form.Item
                    name="artistIdStrm"
                    label={t("pages.ReleasesRanking.artist")}
                  >
                    <Select
                      allowClear
                      filterOption={false}
                      tagRender={tagRender}
                      // suffixIcon={<SearchOutlined />}
                      onSearch={debounce(getArtists, 1000)}
                      onSelect={onChangeArtist}
                      style={{ width: "100% " }}
                      mode="multiple"
                      maxTagCount="responsive"
                      notFoundContent={
                        <Loader.NotFoundLoader loading={loadingArtist} />
                      }
                      placeholder={t("pages.ArtistsRanking.typeHere")}
                    >
                      {artistsOptions.map((artist: any) => (
                        <Select.Option
                          value={artist.id}
                          key={artist.id}
                          label={artist.name}
                        >
                          {artist.image && (
                            <Avatar
                              style={{
                                marginRight: 6,
                                marginTop: 2,
                                marginBottom: 2,
                                width: 18,
                                height: 18,
                              }}
                              src={artist.image}
                            />
                          )}
                          <span className="artist-option">{artist.name}</span>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={4}>
                  <div className="filter-button">
                    <span className="delete" onClick={onDeleteClick}>
                      {t("pages.ArtistsRanking.delete")}
                    </span>
                    <button
                      className="apply"
                      disabled={loading || loadingLocal}
                    >
                      {t("pages.ArtistsRanking.filter")}
                    </button>
                  </div>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col xs={24} md={5}>
                  <Form.Item
                    name="releaseType"
                    label={t("pages.TracksRanking.releaseType")}
                  >
                    <Select placeholder={t("pages.ArtistsRanking.selectHere")}>
                      <Select.Option value="album">
                        {t("pages.ReleasesRanking.album")}
                      </Select.Option>
                      <Select.Option value="single">
                        {t("pages.ReleasesRanking.single")}
                      </Select.Option>
                      <Select.Option value="compilation">
                        {t("pages.ReleasesRanking.compilation")}
                      </Select.Option>
                      <Select.Option value="all">
                        {t("pages.ReleasesRanking.all")}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={5}>
                  <Form.Item
                    name="releases"
                    label={t("pages.ReleasesRanking.release")}
                  >
                    <Select
                      mode="multiple"
                      filterOption={(input, option) =>
                        (option?.value as string)
                          .toUpperCase()
                          .includes(input.toUpperCase()) ||
                        option?.value === "all"
                      }
                      onSearch={debounce(getRelease, 1000)}
                      onDropdownVisibleChange={(open) => setReleaseSearch("")}
                      onSelect={onSelectRelease}
                      notFoundContent={
                        <Loader.NotFoundLoader loading={loadingReleases} />
                      }
                      allowClear
                      maxTagCount="responsive"
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    >
                      {releaseOptions?.length > 0 && (
                        <Select.Option key="all" value="all">
                          {t("pages.LabelsRanking.selectAll")}
                        </Select.Option>
                      )}
                      {releaseOptions.map((option: any) => (
                        <Select.Option value={option} key={`option_${option}`}>
                          {option}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={5}>
                  <Form.Item
                    name="releaseAgeTimeFrame"
                    label={t("pages.TracksRanking.period")}
                  >
                    <Select
                      allowClear
                      onChange={(value) =>
                        setReleaseAgeTimeFrameFieldValue(value)
                      }
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    >
                      {timeFrameOptions.map((option, index) => (
                        <Select.Option value={index} key={`release_${index}`}>
                          {t(`pages.TracksRanking.${option}`)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col md={6}>
                  <Form.Item
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.releaseAgeTimeFrame !==
                      currentValues.releaseAgeTimeFrame
                    }
                    noStyle
                  >
                    {({ getFieldValue }) => {
                      return getFieldValue("releaseAgeTimeFrame") ===
                        TIME_FRAME_OPTION_CUSTOM ? (
                        <Form.Item
                          name={"releaseAgeTimeCustom"}
                          label={t("pages.TracksRanking.setPeriod")}
                        >
                          <RangePicker
                            locale={getLocaleDatePickerByI18n(i18n.language)}
                            format="DD/MM/YYYY"
                          />
                        </Form.Item>
                      ) : null;
                    }}
                  </Form.Item>
                </Col>
                <Col xs={24} md={3}>
                  <div className="filter-button">
                    <span
                      className="delete"
                      style={{ display: "flex", gap: 8 }}
                      onClick={() =>
                        setShowAudioFeaturesFilter(!showAudioFeaturesFilter)
                      }
                    >
                      {t("pages.TracksRanking.seeMore")}
                      {showAudioFeaturesFilter ? (
                        <FontAwesomeIcon icon={faChevronUp} />
                      ) : (
                        <FontAwesomeIcon icon={faChevronDown} />
                      )}
                    </span>
                  </div>
                </Col>
              </Row>
              <Row
                gutter={24}
                style={{
                  height: showAudioFeaturesFilter ? 70 : 0,
                  transition: "height 0.35s ease-in",
                }}
                className="transition-height"
              >
                <Col
                  xs={24}
                  md={4}
                  style={{
                    visibility: showAudioFeaturesFilter ? "unset" : "hidden",
                  }}
                >
                  <Form.Item name="tempo" label={t("pages.Radar.tempo")}>
                    <Slider
                      min={1}
                      max={250}
                      range
                      marks={{
                        1: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>1</strong>,
                        },
                        250: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>250</strong>,
                        },
                      }}
                      tooltipPlacement="bottom"
                    />
                  </Form.Item>
                </Col>
                <Col
                  xs={24}
                  md={4}
                  style={{
                    visibility: showAudioFeaturesFilter ? "unset" : "hidden",
                  }}
                >
                  <Form.Item
                    name="danceabilityLevel"
                    label={t("pages.Radar.danceability")}
                  >
                    <Select
                      allowClear
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    >
                      <Select.Option value="low">
                        {t("pages.TracksRanking.low")}
                      </Select.Option>
                      <Select.Option value="medium">
                        {t("pages.TracksRanking.medium")}
                      </Select.Option>
                      <Select.Option value="high">
                        {t("pages.TracksRanking.high")}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col
                  xs={24}
                  md={4}
                  style={{
                    visibility: showAudioFeaturesFilter ? "unset" : "hidden",
                  }}
                >
                  <Form.Item name="energyLevel" label={t("pages.Radar.energy")}>
                    <Select
                      allowClear
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    >
                      <Select.Option value="low">
                        {t("pages.TracksRanking.low")}
                      </Select.Option>
                      <Select.Option value="medium">
                        {t("pages.TracksRanking.medium")}
                      </Select.Option>
                      <Select.Option value="high">
                        {t("pages.TracksRanking.high")}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col
                  xs={24}
                  md={4}
                  style={{
                    visibility: showAudioFeaturesFilter ? "unset" : "hidden",
                  }}
                >
                  <Form.Item
                    name="valenceLevel"
                    label={t("pages.Radar.valence")}
                  >
                    <Select
                      allowClear
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    >
                      <Select.Option value="low">
                        {t("pages.TracksRanking.low")}
                      </Select.Option>
                      <Select.Option value="medium">
                        {t("pages.TracksRanking.medium")}
                      </Select.Option>
                      <Select.Option value="high">
                        {t("pages.TracksRanking.high")}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col
                  xs={24}
                  md={4}
                  style={{
                    visibility: showAudioFeaturesFilter ? "unset" : "hidden",
                  }}
                >
                  <Form.Item
                    name="acousticnessLevel"
                    label={t("pages.Radar.acousticness")}
                  >
                    <Select
                      allowClear
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    >
                      <Select.Option value="low">
                        {t("pages.TracksRanking.low")}
                      </Select.Option>
                      <Select.Option value="medium">
                        {t("pages.TracksRanking.medium")}
                      </Select.Option>
                      <Select.Option value="high">
                        {t("pages.TracksRanking.high")}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col
                  xs={24}
                  md={4}
                  style={{
                    visibility: showAudioFeaturesFilter ? "unset" : "hidden",
                  }}
                >
                  <Form.Item
                    name="livenessLevel"
                    label={t("pages.Radar.liveness")}
                  >
                    <Select
                      allowClear
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    >
                      <Select.Option value="low">
                        {t("pages.TracksRanking.low")}
                      </Select.Option>
                      <Select.Option value="medium">
                        {t("pages.TracksRanking.medium")}
                      </Select.Option>
                      <Select.Option value="high">
                        {t("pages.TracksRanking.high")}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Form>
        </Col>
      </Row>
      <Row style={{ marginBottom: 16, marginTop: 16 }}>
        <Col
          xs={24}
          md={24}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <Row justify="center">
            <Radio.Group onChange={onChangeIndex} value={rankingIndex}>
              <Radio.Button value={TrackRankingIndex.Popularity}>
                <Tooltip
                  title={t("pages.TracksRanking.popularityExplaination")}
                >
                  {t("pages.TracksRanking.popularity")}
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={TrackRankingIndex.CurrentPopularity}>
                <Tooltip
                  title={t("pages.TracksRanking.currentPopularityExplaination")}
                >
                  {t("pages.TracksRanking.currentPopularity")}
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={TrackRankingIndex.Stability}>
                <Tooltip title={t("pages.TracksRanking.stabilityExplaination")}>
                  {t("pages.TracksRanking.stability")}
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={TrackRankingIndex.Top200}>
                <Tooltip title={t("pages.TracksRanking.top200Explaination")}>
                  {t("pages.TracksRanking.top200")}
                </Tooltip>
              </Radio.Button>
              <Radio.Button value={TrackRankingIndex.Top1000}>
                <Tooltip title={t("pages.TracksRanking.top1000Explaination")}>
                  {t("pages.TracksRanking.top1000")}
                </Tooltip>
              </Radio.Button>
            </Radio.Group>
          </Row>
          <Row
            justify="center"
            style={{
              visibility: enableRangeFilter(rankingIndex)
                ? "visible"
                : "hidden",
            }}
          >
            <Radio.Group
              defaultValue="week"
              value={rangeFilter}
              onChange={onChangeRangeFilter}
              style={{ marginBottom: 0 }}
            >
              <Radio.Button value={"week"}>
                {t("pages.TracksRanking.1w")}
              </Radio.Button>
              <Radio.Button
                value={"month"}
                disabled={
                  releaseAgeTimeFrameFieldValue < 2 &&
                  releaseAgeTimeFrameFieldValue !== ""
                }
              >
                {t("pages.TracksRanking.1m")}
              </Radio.Button>
              <Radio.Button
                value={"trimester"}
                disabled={
                  releaseAgeTimeFrameFieldValue < 3 &&
                  releaseAgeTimeFrameFieldValue !== ""
                }
              >
                {t("pages.TracksRanking.3m")}
              </Radio.Button>
              <Radio.Button
                value={"semester"}
                disabled={
                  releaseAgeTimeFrameFieldValue < 4 &&
                  releaseAgeTimeFrameFieldValue !== ""
                }
              >
                {t("pages.TracksRanking.6m")}
              </Radio.Button>
              <Radio.Button
                value={"year"}
                disabled={
                  releaseAgeTimeFrameFieldValue < 5 &&
                  releaseAgeTimeFrameFieldValue !== ""
                }
              >
                {t("pages.TracksRanking.1y")}
              </Radio.Button>
            </Radio.Group>
            <Popover
              content={t("pages.ReleasesRanking.setPeriodExplain")}
              placement="right"
              style={{ color: "white" }}
            >
              <InfoCircleOutlined
                style={{
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "10px",
                }}
              />
            </Popover>
          </Row>
        </Col>
      </Row>{" "}
      <Row justify="end" style={{ gap: 12 }}>
        {enableExport && (
          <div className="container-botoes-exportar">
            <button
              className="button-export"
              onClick={() => downloadRankingReportCSV()}
            >
              CSV
            </button>
            <button
              className="button-export"
              onClick={() => downloadRankingReportExcel()}
            >
              Excel
            </button>
          </div>
        )}
        <button
          className="botao-azul button-export"
          onClick={() => setEnableExport(!enableExport)}
        >
          {t("pages.ArtistProfile.export")}
          <ExportIcon className="download-icon" />
        </button>
      </Row>
      <Row style={{ marginTop: 16 }}>
        <Col xs={24} md={24}>
          <Row style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <Typography.Title
                level={4}
                style={{ color: "white", textAlign: "left", display: "flex" }}
              >
                {t("pages.TracksRanking.results", {
                  total: ptBRFormat(
                    tracksRanking[`${rankingIndex}`]?.total || 0
                  ),
                })}
                <Popover
                  content={t("pages.TracksRanking.resultsDisclaimer")}
                  placement="right"
                >
                  <InfoCircleOutlined
                    style={{
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      marginLeft: "10px",
                    }}
                  />
                </Popover>
              </Typography.Title>
            </Col>
            <Col xs={24} md={12}>
              <Typography.Title
                level={5}
                style={{ color: "white", textAlign: "right" }}
              >
                {t("pages.ArtistsRanking.lastUpdate")}
                <b>
                  {`${
                    formatDate(tracksRanking[`${rankingIndex}`]?.rankingDate) ||
                    ""
                  } `}
                </b>
              </Typography.Title>
            </Col>
          </Row>

          <Table
            columns={colunas}
            dataSource={tracksRanking[`${rankingIndex}`]?.tracks || []}
            loading={loading || loadingLocal}
            pagination={false}
            rowKey={(row: any) => `track_${row.position}`}
            scroll={{ x: 1300 }}
          />

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Pagination
              style={{ alignSelf: "flex-end", margin: "16px" }}
              hideOnSinglePage={true}
              showSizeChanger={false}
              onChange={(page) => {
                const saveFilter = {
                  ...filter,
                  offset: page * 50 - 50,
                  limit: 50,
                };

                localStorage.setItem(
                  RADAR_TRACKS_RANKING_FILTER,
                  JSON.stringify(saveFilter)
                );
                setFilter(saveFilter);
              }}
              current={filter.offset / filter.limit + 1}
              pageSize={filter.limit}
              total={tracksRanking[`${rankingIndex}`]?.total}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

function tagRender(props: any) {
  const { label, closable, onClose } = props;
  const onPreventMouseDown = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 3, marginBottom: 3 }}
    >
      {label}
    </Tag>
  );
}

export default TracksRanking;
